import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SearchableSelectOption = { value: string; label: string };

/**
 * A text-filterable dropdown that behaves like a native <select> for keyboard/value purposes,
 * but lets the user type to narrow the option list. Used in place of <select> throughout the
 * app so every dropdown gets the same single, consistent chevron and the same search behavior,
 * rather than each screen styling (or not styling) its own native select.
 */
export function SearchableSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  required,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  const selected = options.find((option) => option.value === value) ?? null;
  const filtered = open
    ? options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // The option list renders through a portal at document.body (positioned by the input's screen
  // coordinates) instead of as an absolutely-positioned child, so it's never clipped by an
  // ancestor with overflow-hidden/overflow-auto -- e.g. a scrollable modal panel.
  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const estimatedHeight = Math.min(filtered.length, 6) * 40 + 8;
    const openUpward =
      rect.bottom + estimatedHeight > window.innerHeight && rect.top > estimatedHeight;
    setPosition({
      top: openUpward ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      openUpward,
    });
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node) &&
        !listRef.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    // Scrolling inside the open listbox itself must not close it -- only a scroll of some
    // ancestor (the page, a modal panel, etc.) invalidates the portal's position and should.
    const onScrollOrResize = (event: Event) => {
      if (listRef.current && event.target instanceof Node && listRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const commit = (option: SearchableSelectOption) => {
    onChange(option.value);
    setOpen(false);
    setQuery('');
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && filtered[activeIndex]) commit(filtered[activeIndex]);
      else setOpen(true);
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        setOpen(false);
        setQuery('');
      }
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        autoComplete="off"
        disabled={disabled}
        required={required}
        readOnly={!open}
        value={open ? query : (selected?.label ?? '')}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(0);
          if (!open) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className="schd-select block min-h-11 w-full cursor-pointer rounded-control border bg-white px-3 disabled:cursor-not-allowed"
      />
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink/55"
        aria-hidden="true"
      />
      {open &&
        createPortal(
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              transform: position.openUpward ? 'translateY(-100%)' : undefined,
            }}
            className="schd-menu-panel z-[80] max-h-60 overflow-auto rounded-control border border-border bg-surface py-1 shadow-card"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-ink/55">No matches</li>
            ) : (
              filtered.map((option, index) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    commit(option);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex min-h-10 cursor-pointer items-center justify-between gap-2 px-3 text-sm ${
                    index === activeIndex ? 'bg-burgundy-soft' : ''
                  } ${option.value === value ? 'font-semibold text-burgundy' : 'text-ink'}`}
                >
                  {option.label}
                  {option.value === value && <Check className="size-4 shrink-0" aria-hidden="true" />}
                </li>
              ))
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
}
